import Konva from "konva";
import consts from "shared/consts";
import * as MathUtils from "./math-utils";
import { Segment1d } from "./math-utils";
import { measureNode } from "./node-utils";
import { equals } from "rambda";

type GuideSegment = [number, number, number, number];

export type GuideLines = {
  [key in MathUtils.HLine | MathUtils.VLine]?: GuideSegment;
};

export type DistanceGuidelines = {
  coord: number;
  segment: MathUtils.Segment1d;
  direction: "horizontal" | "vertical";
}[];

type SnapCandidate = {
  coord: number;
  segment: Segment1d;
  position: string;
  node: string;
};

type SnapCandidateWithDistance = SnapCandidate & { distance: number; signedDistance: number };

export type AllSnapCandidates = {
  horizontal: SnapCandidate[];
  vertical: SnapCandidate[];
};

enum AlignMode {
  Edge = "edge",
  Center = "center",
  Both = "both",
}

export function rotate(x: number, y: number, rad: number, pivotX = 0, pivotY = 0) {
  const cos = Math.cos(rad);
  const sin = Math.sin(rad);
  return [cos * (x - pivotX) - sin * (y - pivotY) + pivotX, sin * (x - pivotX) + cos * (y - pivotY) + pivotY];
}

export function isCorner(anchor: string) {
  return anchor == "top-left" || anchor == "top-right" || anchor == "bottom-left" || anchor == "bottom-right";
}

function _getAnchorPos(anchor: string, box: any) {
  switch (anchor) {
    case "top-left": {
      return { x: 0, y: 0 };
    }
    case "top-center": {
      return { x: box.width / 2, y: 0 };
    }
    case "top-right": {
      return { x: box.width, y: 0 };
    }
    case "middle-left": {
      return { x: 0, y: box.height / 2 };
    }
    case "middle-right": {
      return { x: box.width, y: box.height / 2 };
    }
    case "bottom-left": {
      return { x: 0, y: box.height };
    }
    case "bottom-center": {
      return { x: box.width / 2, y: box.height };
    }
    case "bottom-right": {
      return { x: box.width, y: box.height };
    }

    default: {
      return { x: box.width / 2, y: box.height / 2 };
    }
  }
}

export function getAnchorPos(anchor: string, box: any, oppositeAnchor = false) {
  let pos = _getAnchorPos(anchor, box);
  if (oppositeAnchor) {
    pos.x = box.width - pos.x;
    pos.y = box.height - pos.y;
  }
  if (box.rotation) {
    const [x, y] = rotate(pos.x, pos.y, box.rotation);
    pos = { x, y };
  }
  return { x: pos.x + box.x, y: pos.y + box.y };
}

function canSnapToNode(node: any) {
  return (
    node.attrs.isCanvasElement &&
    !node.attrs.isConnector &&
    !node.attrs.isFrameTitle &&
    !node.attrs.isMindmap &&
    node.attrs.element.type !== consts.CANVAS_ELEMENTS.TIMELINE &&
    node.attrs.element.type !== consts.CANVAS_ELEMENTS.GANTT &&
    node.attrs.element.type !== consts.CANVAS_ELEMENTS.ORG_CHART &&
    node.attrs.element.type !== consts.CANVAS_ELEMENTS.ORG_CHART_NODE &&
    !node.attrs.element.containerId
  );
}

/**
 * Returns all snap lines for every element in the viewport.
 * @param layer Konva layer to search for elements
 * @param excludeFromSearch Array of nodes or strings to exclude
 * @returns An objet containint a list of horizontal and a list of vertical snap lines
 */
export function getSnappingGuidelines(
  layer: Konva.Layer | null,
  excludeFromSearch: Konva.Node[] | string[]
): AllSnapCandidates {
  if (!layer) {
    throw Error("Layer is null");
  }
  const viewport = {
    x: 0,
    y: 0,
    ...layer.getStage().getSize(),
  };
  let snapTargets;
  if (excludeFromSearch.length == 0) {
    snapTargets = layer.find(
      (i: any) => canSnapToNode(i) && Konva.Util.haveIntersection(viewport, i.getClientRect({ skipShadow: true }))
    );
  } else if (typeof excludeFromSearch[0] == "string") {
    snapTargets = layer.find(
      (i: any) =>
        canSnapToNode(i) &&
        !excludeFromSearch.includes(i.attrs.id) &&
        Konva.Util.haveIntersection(viewport, i.getClientRect({ skipShadow: true }))
    );
  } else {
    snapTargets = layer.find(
      (i: any) =>
        canSnapToNode(i) &&
        !excludeFromSearch.includes(i) &&
        Konva.Util.haveIntersection(viewport, i.getClientRect({ skipShadow: true }))
    );
  }
  return calcSnappingGuidelines(snapTargets);
}

/**
 * Calculates the 6 snap lines for every node in snapTargets
 * @param snapTargets
 * @returns An objet containint a list of horizontal and a list of vertical snap lines
 */
function calcSnappingGuidelines(snapTargets: Konva.Collection<Konva.Node>): AllSnapCandidates {
  const horizontal: SnapCandidate[] = [],
    vertical: SnapCandidate[] = [];
  const measureOptions = { skipShadow: true };
  for (const node of snapTargets) {
    const { top, bottom, left, right, middle, center } = MathUtils.rectLines(measureNode(node, measureOptions));
    horizontal.push({ coord: top, segment: [left, right], position: "top", node: node.id() });
    horizontal.push({ coord: bottom, segment: [left, right], position: "bottom", node: node.id() });
    horizontal.push({ coord: middle, segment: [left, right], position: "middle", node: node.id() });
    vertical.push({ coord: left, segment: [top, bottom], position: "left", node: node.id() });
    vertical.push({ coord: right, segment: [top, bottom], position: "right", node: node.id() });
    vertical.push({ coord: center, segment: [top, bottom], position: "center", node: node.id() });
  }

  return { horizontal, vertical };
}

export function findClosestSnapLine(
  coord: number,
  lines: SnapCandidate[],
  alignMode = AlignMode.Both
): SnapCandidateWithDistance | null {
  if (alignMode == AlignMode.Edge) lines = lines.filter((l) => l.position !== "middle" && l.position !== "center");
  if (alignMode == AlignMode.Center) lines = lines.filter((l) => l.position === "middle" || l.position === "center");

  let signedDistance = 0,
    minDistance = Number.MAX_SAFE_INTEGER,
    minIndex = -1;
  for (let i = 0; i < lines.length; i++) {
    const dist = Math.abs(coord - lines[i].coord);
    if (dist < minDistance) {
      signedDistance = coord - lines[i].coord;
      minDistance = dist;
      minIndex = i;
    }
  }
  return minIndex == -1 ? null : { ...lines[minIndex], distance: minDistance, signedDistance };
}

export function checkForSnaps(rect: MathUtils.IRect, snapData: AllSnapCandidates) {
  const bbox = MathUtils.rectLines(rect);
  const { horizontal, vertical } = snapData;
  const distanceSnapData = calcDistanceSnapCandidates(bbox, snapData);
  let horizSnap = null;
  let vertSnap = null;

  const { horizCenter, vertCenter } = checkCenterElementsSnap(bbox, distanceSnapData);
  const { horizRow, vertRow } = checkElementRowSnap(bbox, snapData, distanceSnapData);

  if (horizontal.length > 0) {
    horizSnap = findClosestSnapLine(bbox.top, horizontal, AlignMode.Edge);
    if (horizSnap) {
      let snap = findClosestSnapLine(bbox.middle, horizontal, AlignMode.Center);
      if (snap && snap.distance < horizSnap!.distance) horizSnap = snap;
      snap = findClosestSnapLine(bbox.bottom, horizontal, AlignMode.Edge);
      if (snap && snap.distance < horizSnap!.distance) horizSnap = snap;
      if (horizCenter && horizCenter.distance < horizSnap.distance) horizSnap = horizCenter;
      if (horizRow && horizRow.distance < horizSnap.distance) horizSnap = horizRow;
    }
  }

  if (vertical.length > 0) {
    vertSnap = findClosestSnapLine(bbox.left, vertical, AlignMode.Edge);
    if (vertSnap) {
      let snap = findClosestSnapLine(bbox.center, vertical, AlignMode.Center);
      if (snap && snap.distance < vertSnap!.distance) vertSnap = snap;
      snap = findClosestSnapLine(bbox.right, vertical, AlignMode.Edge);
      if (snap && snap.distance < vertSnap!.distance) vertSnap = snap;
      if (vertCenter && vertCenter.distance < vertSnap.distance) vertSnap = vertCenter;
      if (vertRow && vertRow.distance < vertSnap.distance) vertSnap = vertRow;
    }
  }

  return { horizSnap, vertSnap };
}

function checkForSnapLine(coord: number, edge: number[], lines: SnapCandidate[], alignMode = AlignMode.Both) {
  if (alignMode == AlignMode.Edge) lines = lines.filter((l) => l.position !== "middle" && l.position !== "center");
  if (alignMode == AlignMode.Center) lines = lines.filter((l) => l.position === "middle" || l.position === "center");

  let segment, distance, origin;
  let minDistance = Number.MAX_SAFE_INTEGER;
  for (const line of lines) {
    if (Math.abs(line.coord - coord) < 1) {
      distance = Math.min(...line.segment.map((p1) => Math.min(...edge.map((p2) => Math.abs(p1 - p2)))));
      if (distance < minDistance) {
        segment = line.segment;
        minDistance = distance;
        origin = line.node;
      }
    }
  }

  if (!segment) return null;
  return { segment, distance, origin };
}

export function checkTransformSnapLines(
  box: MathUtils.IRect,
  snapData: AllSnapCandidates,
  activeAnchor: string,
  centered: boolean
) {
  const { horizontal, vertical } = snapData;
  let horizSnap = null;
  let vertSnap = null;

  const { horizSizeSnap, vertSizeSnap } = checkElementSizeSnap(box, snapData, activeAnchor);

  if (horizontal.length > 0) {
    horizSnap = findClosestSnapLine(box.y, snapData.horizontal);
    if (horizSnap && horizSizeSnap && horizSizeSnap.distance < horizSnap.distance) horizSnap = horizSizeSnap;
  }

  if (vertical.length > 0) {
    vertSnap = findClosestSnapLine(box.x, snapData.vertical);
    if (vertSnap && vertSizeSnap && vertSizeSnap.distance < vertSnap?.distance) vertSnap = vertSizeSnap;
  }

  // Centered means both sides are resizing, so distance is halved and point should be adjusted
  if (centered) {
    if (horizSnap) {
      horizSnap.coord += horizSnap.signedDistance / 2;
      horizSnap.signedDistance /= 2;
    }
    if (vertSnap) {
      vertSnap.coord += vertSnap.signedDistance / 2;
      vertSnap.signedDistance /= 2;
    }
  }

  return { horizSnap, vertSnap };
}

function checkElementSizeSnap(box: MathUtils.IRect, snapData: AllSnapCandidates, activeAnchor: string) {
  const xModifier = activeAnchor.includes("right") ? 1 : -1;
  const yModifier = activeAnchor.includes("bottom") ? 1 : -1;

  const widthCandidates: SnapCandidate[] = snapData.horizontal
    .filter((line) => line.position === "bottom")
    .map((line) => {
      const width = line.segment[1] - line.segment[0];
      const coord = box.x + (width - box.width) * xModifier;
      return { ...line, segment: [coord, coord], coord };
    });
  const vertSizeSnap = findClosestSnapLine(box.x, widthCandidates);

  const heightCandidates: SnapCandidate[] = snapData.vertical
    .filter((line) => line.position === "right")
    .map((line) => {
      const height = line.segment[1] - line.segment[0];
      const coord = box.y + (height - box.height) * yModifier;
      return { ...line, segment: [coord, coord], coord };
    });
  const horizSizeSnap = findClosestSnapLine(box.y, heightCandidates);

  return { horizSizeSnap, vertSizeSnap };
}

export function getSnapLinesForPoint(p: MathUtils.Point, snapData: AllSnapCandidates): GuideLines {
  let { horizontal, vertical } = snapData,
    guides: any = {},
    horizLine = checkForSnapLine(p.y, [p.y], horizontal, AlignMode.Both)?.segment,
    vertLine = checkForSnapLine(p.x, [p.x], vertical, AlignMode.Both)?.segment;
  if (horizLine) {
    const [x1, x2] = MathUtils.union1dSegments(horizLine, [p.x, p.x]);
    guides["middle"] = [x1, p.y, x2, p.y];
  }
  if (vertLine) {
    const [y1, y2] = MathUtils.union1dSegments(vertLine, [p.y, p.y]);
    guides["center"] = [p.x, y1, p.x, y2];
  }

  return guides;
}

export function getSnapLines(box: MathUtils.IRect, snapData: AllSnapCandidates): GuideLines {
  let horizGuides: any[] = [];
  let vertGuides: any[] = [];
  const { horizontal, vertical } = snapData;
  const bbox = MathUtils.rectLines(box);

  let snapLine = checkForSnapLine(bbox.top, [bbox.left, bbox.right], horizontal, AlignMode.Edge);
  if (snapLine) {
    const [, index] = MathUtils.getClosestPointTo1dSegment(snapLine.segment, [bbox.left, bbox.right]);
    const [x1, x2] = MathUtils.extend1dSegment(snapLine.segment, [bbox.left, bbox.right][index]);
    vertGuides.push({ side: "top", line: [x1, bbox.top, x2, bbox.top], origin: snapLine.origin });
  }

  snapLine = checkForSnapLine(bbox.bottom, [bbox.left, bbox.right], horizontal, AlignMode.Edge);
  if (snapLine) {
    const [, index] = MathUtils.getClosestPointTo1dSegment(snapLine.segment, [bbox.left, bbox.right]);
    const [x1, x2] = MathUtils.extend1dSegment(snapLine.segment, [bbox.left, bbox.right][index]);
    vertGuides.push({ side: "bottom", line: [x1, bbox.bottom, x2, bbox.bottom], origin: snapLine.origin });
  }

  snapLine = checkForSnapLine(bbox.middle, [bbox.left, bbox.right], horizontal, AlignMode.Center);
  if (snapLine) {
    const [x1, x2] = MathUtils.union1dSegments(snapLine.segment, [bbox.left, bbox.right]);
    vertGuides.push({ side: "middle", line: [x1, bbox.middle, x2, bbox.middle], origin: snapLine.origin });
  }

  snapLine = checkForSnapLine(bbox.left, [bbox.top, bbox.bottom], vertical, AlignMode.Edge);
  if (snapLine) {
    const [, index] = MathUtils.getClosestPointTo1dSegment(snapLine.segment, [bbox.top, bbox.bottom]);
    const [y1, y2] = MathUtils.extend1dSegment(snapLine.segment, [bbox.top, bbox.bottom][index]); // in Konva coordinates, top<bottom
    horizGuides.push({ side: "left", line: [bbox.left, y1, bbox.left, y2], origin: snapLine.origin });
  }

  snapLine = checkForSnapLine(bbox.right, [bbox.top, bbox.bottom], vertical, AlignMode.Edge);
  if (snapLine) {
    const [, index] = MathUtils.getClosestPointTo1dSegment(snapLine.segment, [bbox.top, bbox.bottom]);
    const [y1, y2] = MathUtils.extend1dSegment(snapLine.segment, [bbox.top, bbox.bottom][index]); // in Konva coordinates, top<bottom
    horizGuides.push({ side: "right", line: [bbox.right, y1, bbox.right, y2], origin: snapLine.origin });
  }

  snapLine = checkForSnapLine(bbox.center, [bbox.top, bbox.bottom], vertical, AlignMode.Center);
  if (snapLine) {
    const [y1, y2] = MathUtils.union1dSegments(snapLine.segment, [bbox.top, bbox.bottom]); // in Konva coordinates, top<bottom
    horizGuides.push({ side: "center", line: [bbox.center, y1, bbox.center, y2], origin: snapLine.origin });
  }

  // Remove center/middle lines if all guides are from the same element
  if (vertGuides.length === 3 && vertGuides.every((guide) => guide.origin === vertGuides[0].origin)) {
    vertGuides = vertGuides.filter((guide) => guide.side !== "middle");
  }
  if (horizGuides.length === 3 && horizGuides.every((guide) => guide.origin === horizGuides[0].origin)) {
    horizGuides = horizGuides.filter((guide) => guide.side !== "center");
  }

  const guidelines: GuideLines = Object.fromEntries(
    [...horizGuides, ...vertGuides].map((guide) => [guide.side, guide.line])
  );
  return guidelines;
}

/**
 * Calculates the distance snap candidates for a given box.
 * @param bbox
 * @param snapData
 * @returns
 */
function calcDistanceSnapCandidates(bbox: MathUtils.RectLines, snapData: AllSnapCandidates) {
  let { horizontal, vertical } = snapData;
  horizontal = horizontal.filter((line) => !!MathUtils.intersect1dSegments([bbox.left, bbox.right], line.segment));
  const { top, bottom } = horizontal.reduce(
    (acc, line) => {
      if (line.coord > bbox.bottom && line.position === "top") {
        acc.bottom.push(line);
      }
      if (line.coord < bbox.top && line.position === "bottom") {
        acc.top.push(line);
      }
      return acc;
    },
    { top: [] as SnapCandidate[], bottom: [] as SnapCandidate[] }
  );

  vertical = vertical.filter((line) => !!MathUtils.intersect1dSegments([bbox.top, bbox.bottom], line.segment));
  const { left, right } = vertical.reduce(
    (acc, line) => {
      if (line.coord > bbox.right && line.position === "left") {
        acc.right.push(line);
      }
      if (line.coord < bbox.left && line.position === "right") {
        acc.left.push(line);
      }
      return acc;
    },
    { left: [] as SnapCandidate[], right: [] as SnapCandidate[] }
  );

  return { left, right, top, bottom };
}

function checkCenterElementsSnap(bbox: MathUtils.RectLines, distanceSnapData: any) {
  let horizCenter: SnapCandidateWithDistance | null = null;
  let vertCenter: SnapCandidateWithDistance | null = null;
  let { left, right, top, bottom } = distanceSnapData;
  const baseSnaplineData: SnapCandidate = { coord: 0, segment: [0, 0], position: "", node: "" };

  left = findClosestSnapLine(bbox.left, left);
  right = findClosestSnapLine(bbox.right, right);
  if (right && left) {
    const signedDistance = (left.distance - right.distance) / 2;
    vertCenter = {
      ...baseSnaplineData,
      distance: Math.abs(signedDistance),
      signedDistance,
    };
  }

  top = findClosestSnapLine(bbox.top, top);
  bottom = findClosestSnapLine(bbox.bottom, bottom);
  if (top && bottom) {
    const signedDistance = (top.distance - bottom.distance) / 2;
    horizCenter = {
      ...baseSnaplineData,
      distance: Math.abs(signedDistance),
      signedDistance,
    };
  }

  return { horizCenter, vertCenter };
}

function checkElementRowSnap(bbox: MathUtils.RectLines, snapData: AllSnapCandidates, distanceSnapData: any) {
  let horizRow: SnapCandidateWithDistance | null = null;
  let vertRow: SnapCandidateWithDistance | null = null;
  const baseSnaplineData: SnapCandidate = { coord: 0, segment: [0, 0], position: "", node: "" };

  for (const direction of ["left", "right", "top", "bottom"]) {
    let line = (bbox as any)[direction];
    line = findClosestSnapLine(line, distanceSnapData[direction]);
    if (!line) continue;
    const next = getNextNodeSnapline(line, snapData);
    if (!next) continue;

    let signedDistance;
    switch (direction) {
      case "left": {
        signedDistance = line.distance - next.distance;
        vertRow = {
          ...baseSnaplineData,
          distance: Math.abs(signedDistance),
          signedDistance,
        };
        break;
      }
      case "right": {
        signedDistance = next.distance - line.distance;
        if (!vertRow || Math.abs(signedDistance) < vertRow.distance) {
          vertRow = {
            ...baseSnaplineData,
            distance: Math.abs(signedDistance),
            signedDistance,
          };
        }
        break;
      }
      case "top": {
        signedDistance = line.distance - next.distance;
        horizRow = {
          ...baseSnaplineData,
          distance: Math.abs(signedDistance),
          signedDistance,
        };
        break;
      }
      case "bottom": {
        signedDistance = next.distance - line.distance;
        if (!horizRow || Math.abs(signedDistance) < horizRow.distance) {
          horizRow = {
            ...baseSnaplineData,
            distance: Math.abs(signedDistance),
            signedDistance,
          };
        }
        break;
      }
    }
  }

  return { horizRow, vertRow };
}

function getNextNodeSnapline(line: SnapCandidate, snapData: AllSnapCandidates) {
  const position = MathUtils.getOppositeSide(line.position, true);
  const bbox: any = getNodeBBoxFromSnapData(line.node, snapData);
  const candidates: any = calcDistanceSnapCandidates(bbox, snapData);
  return findClosestSnapLine(bbox[position], candidates[position] ?? []);
}

function getNodeBBoxFromSnapData(nodeId: string, snapData: AllSnapCandidates): MathUtils.RectLines {
  const left = snapData.vertical.find((line) => line.node === nodeId && line.position === "left")!.coord;
  const right = snapData.vertical.find((line) => line.node === nodeId && line.position === "right")!.coord;
  const top = snapData.horizontal.find((line) => line.node === nodeId && line.position === "top")!.coord;
  const bottom = snapData.horizontal.find((line) => line.node === nodeId && line.position === "bottom")!.coord;
  return { top, bottom, left, right, middle: bottom - (bottom - top) / 2, center: right - (right - left) / 2 };
}

function getOppositeSnapLineCoord(line: SnapCandidate, snapData: AllSnapCandidates): number {
  const bbox: any = getNodeBBoxFromSnapData(line.node, snapData);
  return bbox[MathUtils.getOppositeSide(line.position, true)];
}

/**
 * Returns a list of lines to draw based on equal distance between elements.
 */
export function getDistanceSnapLines(box: MathUtils.IRect, snapData: AllSnapCandidates) {
  const lines: DistanceGuidelines = [];
  const bbox = MathUtils.rectLines(box);
  const distanceSnapData = calcDistanceSnapCandidates(bbox, snapData);
  const nearestLines = {
    left: findClosestSnapLine(bbox.left, distanceSnapData.left),
    right: findClosestSnapLine(bbox.right, distanceSnapData.right),
    top: findClosestSnapLine(bbox.top, distanceSnapData.top),
    bottom: findClosestSnapLine(bbox.bottom, distanceSnapData.bottom),
  };

  if (nearestLines.left && nearestLines.right) {
    const signedDistance = (nearestLines.left.distance - nearestLines.right.distance) / 2;
    if (Math.abs(signedDistance) < 1) {
      const rightY = MathUtils.getCenterOf1dSegmentIntersect([bbox.top, bbox.bottom], nearestLines.right.segment);
      const leftY = MathUtils.getCenterOf1dSegmentIntersect([bbox.top, bbox.bottom], nearestLines.left.segment);
      lines.push({ coord: rightY, segment: [bbox.right, nearestLines.right.coord], direction: "horizontal" });
      lines.push({ coord: leftY, segment: [bbox.left, nearestLines.left.coord], direction: "horizontal" });
    }
  }

  if (nearestLines.top && nearestLines.bottom) {
    const signedDistance = (nearestLines.top.distance - nearestLines.bottom.distance) / 2;
    if (Math.abs(signedDistance) < 1) {
      const topX = MathUtils.getCenterOf1dSegmentIntersect([bbox.left, bbox.right], nearestLines.top.segment);
      const bottomX = MathUtils.getCenterOf1dSegmentIntersect([bbox.left, bbox.right], nearestLines.bottom.segment);
      lines.push({ coord: topX, segment: [bbox.top, nearestLines.top.coord], direction: "vertical" });
      lines.push({ coord: bottomX, segment: [bbox.bottom, nearestLines.bottom.coord], direction: "vertical" });
    }
  }

  for (const direction of ["left", "right", "top", "bottom"]) {
    let line = (nearestLines as any)[direction];
    if (line) {
      let partOfChain = true;
      let chainLength = 0;
      let nextLine = getNextNodeSnapline(line, snapData);
      while (partOfChain && nextLine && chainLength < 100) {
        if (Math.abs(nextLine.distance - line.distance) < 1) {
          // Push first line if needed
          if (equals(line, (nearestLines as any)[direction])) {
            if (["left", "right"].includes(direction)) {
              const y = MathUtils.getCenterOf1dSegmentIntersect([bbox.top, bbox.bottom], line.segment);
              lines.push({ coord: y, segment: [(bbox as any)[direction], line.coord], direction: "horizontal" });
            } else {
              const x = MathUtils.getCenterOf1dSegmentIntersect([bbox.left, bbox.right], line.segment);
              lines.push({ coord: x, segment: [(bbox as any)[direction], line.coord], direction: "vertical" });
            }
          }

          const lineCoord = MathUtils.getCenterOf1dSegmentIntersect(line.segment, nextLine.segment);
          if (["left", "right"].includes(direction)) {
            lines.push({
              coord: lineCoord,
              segment: [getOppositeSnapLineCoord(line, snapData), nextLine.coord],
              direction: "horizontal",
            });
          } else {
            lines.push({
              coord: lineCoord,
              segment: [getOppositeSnapLineCoord(line, snapData), nextLine.coord],
              direction: "vertical",
            });
          }

          line = nextLine;
          nextLine = getNextNodeSnapline(line, snapData);
        } else {
          partOfChain = false;
        }
        chainLength++;
      }
    }
  }

  return lines;
}

export function getSizeSnapLines(box: MathUtils.IRect, snapData: AllSnapCandidates, activeAnchor: string) {
  const lines: DistanceGuidelines = [];

  if (activeAnchor.includes("left") || activeAnchor.includes("right")) {
    const widthCandidates: SnapCandidate[] = snapData.horizontal
      .filter((line) => line.position === "bottom")
      .filter((line) => Math.abs(line.segment[1] - line.segment[0] - box.width) < 1);
    if (widthCandidates.length > 0) {
      widthCandidates.push({
        coord: box.y + box.height,
        segment: [box.x, box.x + box.width],
        position: "bottom",
        node: "",
      });
    }
    widthCandidates.forEach((line) =>
      lines.push({ coord: line.coord, segment: line.segment, direction: "horizontal" })
    );
  }

  if (activeAnchor.includes("top") || activeAnchor.includes("bottom")) {
    const heightCandidates: SnapCandidate[] = snapData.vertical
      .filter((line) => line.position === "right")
      .filter((line) => Math.abs(line.segment[1] - line.segment[0] - box.height) < 1);
    if (heightCandidates.length > 0) {
      heightCandidates.push({
        coord: box.x + box.width,
        segment: [box.y, box.y + box.height],
        position: "right",
        node: "",
      });
    }
    heightCandidates.forEach((line) => lines.push({ coord: line.coord, segment: line.segment, direction: "vertical" }));
  }

  return lines;
}

/**
 * Renders guidelines directly to a Konva layer
 */
export function renderGuidelinesDirectly(
  overlayLayer: Konva.Layer | null,
  guidelines: GuideLines | null,
  sizeGuidelines: DistanceGuidelines | null,
  scale: number,
  className: string = "snap-guide"
) {
  if (!overlayLayer) return;

  // Clear previous guidelines with this class name
  const existingNodes = overlayLayer.find(`.${className}`);
  for (const node of existingNodes) {
    node.destroy();
  }

  // Render regular snap guidelines
  if (guidelines) {
    for (const [_key, value] of Object.entries(guidelines)) {
      if (
        !Array.isArray(value) ||
        value.length !== 4 ||
        !value.every((v) => typeof v === "number" && !Number.isNaN(v))
      ) {
        continue;
      }

      const guideLine = new Konva.Line({
        points: value as number[],
        strokeWidth: 1 / scale,
        stroke: "#00A1FF",
        dash: [3, 3],
        name: className,
        listening: false,
      });
      overlayLayer.add(guideLine);
    }
  }

  // Render size/distance guidelines
  if (sizeGuidelines && Array.isArray(sizeGuidelines)) {
    for (const line of sizeGuidelines) {
      const margin = 5;
      const adornmentSize = 5;
      const coord = line.coord;
      const segment = line.segment;
      const direction = line.direction;

      const start = Math.min(...segment) + margin;
      const end = Math.max(...segment) - margin;

      let mainLine, startLine, endLine;
      if (direction === "horizontal") {
        mainLine = [start, coord, end, coord];
        startLine = [start, coord - adornmentSize, start, coord + adornmentSize];
        endLine = [end, coord - adornmentSize, end, coord + adornmentSize];
      } else {
        mainLine = [coord, start, coord, end];
        startLine = [coord - adornmentSize, start, coord + adornmentSize, start];
        endLine = [coord - adornmentSize, end, coord + adornmentSize, end];
      }

      for (const points of [mainLine, startLine, endLine]) {
        if (
          !Array.isArray(points) ||
          points.length !== 4 ||
          !points.every((v) => typeof v === "number" && !Number.isNaN(v))
        ) {
          continue;
        }

        const distanceLine = new Konva.Line({
          points,
          strokeWidth: 1 / scale,
          stroke: "#00A1FF",
          name: className,
          listening: false,
        });
        overlayLayer.add(distanceLine);
      }
    }
  }

  overlayLayer.batchDraw();
}

/**
 * Clears all guidelines from a Konva layer
 */
export function clearGuidelines(overlayLayer: Konva.Layer | null, className: string = "snap-guide") {
  if (!overlayLayer) return;

  const nodes = overlayLayer.find(`.${className}`);
  for (const node of nodes) {
    node.destroy();
  }
  overlayLayer.batchDraw();
}
